<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { Check, RefreshCw, Save, Search, UsersRound } from 'lucide-vue-next'

import type { AuthorizationState, Channel, ChannelConfig, TelegramDialogOption } from '@/types/monitor'

const props = defineProps<{
  dialogs: TelegramDialogOption[]
  channels: Channel[]
  loading: boolean
  saving: boolean
  authorization: AuthorizationState
  telegramEnabled: boolean
}>()

const emit = defineEmits<{
  reload: []
  save: [channels: ChannelConfig[]]
  authenticate: []
}>()

const { t } = useI18n()
const query = ref('')
const kind = ref<'all' | 'channel' | 'group'>('all')
const selectedIds = ref<Set<number>>(new Set())

watch(
  () => props.dialogs,
  (dialogs) => {
    selectedIds.value = new Set(dialogs.filter((dialog) => dialog.selected).map((dialog) => dialog.telegram_id))
  },
  { immediate: true, deep: true },
)

const filtered = computed(() => {
  const needle = query.value.trim().toLowerCase()
  return props.dialogs.filter((dialog) => {
    if (kind.value !== 'all' && dialog.kind !== kind.value) return false
    if (!needle) return true
    return `${dialog.title} ${dialog.username ?? ''} ${dialog.telegram_id}`.toLowerCase().includes(needle)
  })
})

function toggle(id: number): void {
  const next = new Set(selectedIds.value)
  if (next.has(id)) next.delete(id)
  else next.add(id)
  selectedIds.value = next
}

function selectVisible(): void {
  const next = new Set(selectedIds.value)
  for (const dialog of filtered.value) next.add(dialog.telegram_id)
  selectedIds.value = next
}

function clear(): void {
  selectedIds.value = new Set()
}

function save(): void {
  const channels = props.dialogs
    .filter((dialog) => selectedIds.value.has(dialog.telegram_id))
    .map<ChannelConfig>((dialog) => ({
      username: dialog.key,
      telegram_id: dialog.telegram_id,
      title: dialog.title,
      category: dialog.kind === 'channel' ? 'Telegram Channel' : 'Telegram Group',
      description: '',
    }))
  emit('save', channels)
}
</script>

<template>
  <section class="tm-view">
    <header class="tm-page-header">
      <div>
        <h1>{{ t('channels.title') }}</h1>
        <p>{{ t('channels.myTelegramHint') }}</p>
      </div>
      <div class="tm-page-actions">
        <button class="tm-button" :disabled="loading || authorization !== 'authorized'" @click="emit('reload')">
          <RefreshCw :size="17" :class="{ spin: loading }" />
          {{ t('common.retry') }}
        </button>
        <button class="tm-button tm-button--primary" :disabled="saving || authorization !== 'authorized'" @click="save">
          <Save :size="17" />{{ t('channels.saveSelection') }}
        </button>
      </div>
    </header>

    <div v-if="!telegramEnabled || authorization !== 'authorized'" class="tm-card tm-auth-callout">
      <UsersRound :size="30" />
      <div><h2>{{ t('telegramAuth.establish') }}</h2><p>{{ t('telegramAuth.phoneHelp') }}</p></div>
      <button class="tm-button tm-button--primary" @click="emit('authenticate')">{{ t('header.authenticate') }}</button>
    </div>

    <template v-else>
      <div class="tm-toolbar tm-source-toolbar">
        <div class="tm-search-field"><Search :size="17" /><input v-model="query" :placeholder="t('channels.filter')" /></div>
        <div class="tm-segmented">
          <button :class="{ active: kind === 'all' }" @click="kind = 'all'">{{ t('channels.allTypes') }}</button>
          <button :class="{ active: kind === 'channel' }" @click="kind = 'channel'">{{ t('channels.channelType') }}</button>
          <button :class="{ active: kind === 'group' }" @click="kind = 'group'">{{ t('channels.groupType') }}</button>
        </div>
        <button class="tm-text-button" @click="selectVisible">{{ t('channels.selectVisible') }}</button>
        <button class="tm-text-button" @click="clear">{{ t('channels.clearSelection') }}</button>
        <span class="tm-selection-count">{{ t('channels.selectedCount', { count: selectedIds.size }) }}</span>
      </div>

      <div class="tm-card tm-source-list-card">
        <div v-if="loading" class="tm-source-skeleton"><el-skeleton :rows="8" animated /></div>
        <div v-else-if="filtered.length" class="tm-source-grid">
          <button
            v-for="dialog in filtered"
            :key="dialog.telegram_id"
            class="tm-source-row"
            :class="{ 'tm-source-row--selected': selectedIds.has(dialog.telegram_id) }"
            @click="toggle(dialog.telegram_id)"
          >
            <span class="tm-source-check"><Check v-if="selectedIds.has(dialog.telegram_id)" :size="16" /></span>
            <div class="tm-source-avatar">{{ dialog.title.slice(0, 1).toUpperCase() }}</div>
            <div class="tm-source-copy">
              <strong>{{ dialog.title }}</strong>
              <span>{{ dialog.username ? `@${dialog.username}` : t('channels.privateDialog') }}</span>
            </div>
            <span class="tm-source-kind">{{ dialog.kind === 'channel' ? t('channels.channelType') : t('channels.groupType') }}</span>
          </button>
        </div>
        <div v-else class="tm-empty-state tm-empty-state--page"><UsersRound :size="36" /><p>{{ t('channels.noDialogs') }}</p></div>
      </div>
    </template>
  </section>
</template>
